import { IStrings, stringsObj } from './strings';
import { ISchema, IComponent, ISelectOptionItem, IDataComponent, IContainerComponent, ISchemaError, IAnyObj } from './types';
import cloneDeep from 'lodash.clonedeep';
// import merge from 'lodash.merge';
import get from 'lodash.get';
import set from 'lodash.set';
import { EventEmitter } from 'events'
import { IDataTableComponent } from '../components/datatable';
import { Color, IThemeColor, ThemeColors } from './themeColors';
import { checkSchema, printErrors } from './checkSchema';

export interface ISettings {
  requiredSuffix: string;
  language: string;
  themeColor: Color;
  strings?: IStrings
}

export interface IError {
  comp: IDataComponent;
  error: string;
  arrayInd?: number; //index of array in data-table
}

interface IValuesDataTable {
  val: any,
  memval: any,
}

export enum EventType {
  RenderComponent = 'RenderComponent',
  FocusComponent = 'FocusComponent',
  OpenModal = 'OpenModal',
  CloseModal = 'CloseModal',
  FoucsDropdown = 'FoucsDropdown',
}

export type ITraverseCallback = (comp: IComponent, parent?: IComponent, options?: ITraverseOptions) => void;

export interface ITraverseOptions {
  done?: boolean;
}

export const flexStyle = {
  display: 'inline-flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '20px'
} as React.CSSProperties


export interface IHighlight {
  comp: IDataComponent;
  arrayInd: number;
}

export enum IValueType {
  undefined = 'undefined',
  null = 'null',
  string = 'string',
  number = 'number',
  boolean = 'boolean',
  date = 'date',
  function = 'function',
  array = 'array',
  object = 'object',
  component = 'component',
}

export class SchemaManager extends EventEmitter {
  Schema: ISchema;
  Values: IAnyObj;
  private CommitValuesDataTable: any;
  MemValues: any
  ValuesChanged: boolean;
  Settings: ISettings;
  Strings: IStrings;
  ThemeColors: IThemeColor;
  Diffsm: SchemaManager;
  DiffClassName: string;
  private curCompId = 1;
  private compIds = new WeakMap();


  Errors: IError[];
  highlightedFields: IHighlight[];
  AllValidated: boolean;

  private _AllDisabled: boolean;
  get AllDisabled(): boolean {
    return this._AllDisabled;
  }

  DisableAll(disabled: boolean = true) {
    if (this._AllDisabled !== disabled) {
      this._AllDisabled = disabled;
    }
  }

  constructor(settings?: Partial<ISettings>) {
    super()
    this.InitSettings(settings);
  }

  InitSettings(settings?: Partial<ISettings>) {
    if (!this.Settings) {
      this.Settings = {
        language: 'en',
        requiredSuffix: ' *',
        themeColor: 'blue',
      }
    }
    if (settings) {
      this.Settings = {
        ...this.Settings,
        ...settings
      }
    }

    if (this.Settings.language && stringsObj[this.Settings.language]) {
      this.Settings.strings = stringsObj[this.Settings.language]
    } else {
      this.Settings.strings = stringsObj['en']
    }
    this.Strings = this.Settings.strings;
    this.ThemeColors = ThemeColors[this.Settings.themeColor]
  }

  InitSchema(schema: ISchema) {
    this.setMaxListeners(0)
    if (SchemaManager.checkValueType(schema) !== IValueType.component) {
      console.error('schema must be an object with a type property: ', schema)
      return;
    }
    this.Schema = cloneDeep(schema);
    // if (this.Schema.inheritFrom) this.InitInherits();
    this.Errors = [];
    this.AllValidated = false;
    if (this.Schema.onInitSchema) this.Schema.onInitSchema({ sm: this, comp: this.Schema });
    this.traverseSchema((c, p) => {
      if (typeof c === 'object') c.parentComp = p;
      if (c.type === 'datatable') {
        this.InitDataTableKeyField(c as IDataTableComponent)
      }
      if (c.type === 'modal') {
        c.hidden = true
      }
    });
    this.InitValues(this.Values);
  }


  InitValues(values: any) {
    if (values) {
      this.MemValues = {};
      this.Values = values;
    } else {
      this.Values = {};
      this.MemValues = {};
    }

    this.traverseSchema(comp => {
      const dt = comp as IDataComponent
      if (comp.type === 'datatable') {
        this.InitValuesDataTable(dt as IDataTableComponent)
      }
      if (dt.field && dt.hasOwnProperty('default') && !this.fieldIsInDataTable(dt)) {
        const val = this.getValue(comp)
        if (typeof val === 'undefined' || val === '' || val === false) {
          this.updateValue(comp, this.getPropValue(comp, 'default'))
        }
      }
    });

    this.Errors = [];
    this.CommitValuesDataTable = {}
    this.AllValidated = false;
    this.ValuesChanged = false;

  }

  InitDiffHighlight() {
    if (!this.Diffsm) return;
    this.highlightedFields = [];
    this.traverseSchema(comp => {
      if (comp.type === 'datatable') {
        const fields = this.getFieldsOfDataTable(comp)
        const { val: data } = this.getValuesDataTable(comp)
        data.forEach((_: any, ind: number) => {
          fields.forEach(f => this.InitDiffHighlightComp(f, ind))
        });
      }
      if (!this.fieldIsInDataTable(comp as IDataComponent)) {
        this.InitDiffHighlightComp(comp);
      }
    });
  }

  InitDiffHighlightComp(comp: IComponent, arrayInd: number = -1): boolean {
    if (!this.Diffsm || !this.highlightedFields || comp.type === 'datatable') return false;
    const cp = comp as IDataComponent

    if (cp.field) {
      if (arrayInd === -1) {
        const dt = this.getParentDataTable(cp)
        if (dt) {
          arrayInd = typeof dt.editRowIndex !== 'undefined' ? dt.editRowIndex : -1
        }
      }

      const val1 = this.getValue(comp, arrayInd)
      const val2 = this.Diffsm.getValue(comp, arrayInd)
      let isEqual = false
      if (SchemaManager.checkValueType(val1) === IValueType.array && SchemaManager.checkValueType(val2) === IValueType.array) {
        isEqual = SchemaManager.compareArray(val1, val2)
      } else {
        isEqual = val1 === val2
      }

      const ind = this.highlightedFields.findIndex(h => h.comp === comp && h.arrayInd === arrayInd)
      if (isEqual) {
        if (ind > -1) {
          this.highlightedFields.splice(ind, 1)
          return true
        }
      } else {
        if (ind === -1) {
          this.highlightedFields.push({ comp: cp, arrayInd })
          return true
        }
      }
    }
    return false
  }

  getDiffHighlight(comp: IComponent, arrayInd: number = -1): boolean {
    if (!this.Diffsm || !this.highlightedFields) return false;
    let h
    if (arrayInd === -1) {
      const dt = this.getParentDataTable(comp as IDataComponent)
      if (dt) {
        arrayInd = typeof dt.editRowIndex !== 'undefined' ? dt.editRowIndex : -1
      }
    }
    h = this.highlightedFields.find(h => h.comp === comp && h.arrayInd === arrayInd);
    return !!h;
  }


  getPropValue(comp: IComponent, prop: string): any {
    if (typeof comp[prop] === 'undefined') {
      return undefined;
    } else if (typeof comp[prop] === 'function') {
      return comp[prop]({ sm: this, comp });
    } else {
      return comp[prop];
    }
  }

  hasNoValue(value: any): boolean {
    const typ = SchemaManager.checkValueType(value);
    let noVal = false;
    if (typ === IValueType.undefined || typ === IValueType.null) {
      noVal = true;
    } else if (typ === IValueType.string && value === '') {
      noVal = true;
    } else if (typ === IValueType.array && value.length === 0) {
      noVal = true;
    }
    return noVal;

  }

  getParentValues(comp: IDataComponent, values: any, arrayInd: number = -1): any {
    const dt = this.getParentDataTable(comp)

    if (dt) {
      const ind: number = arrayInd > -1 ? arrayInd : (typeof dt.editRowIndex !== 'undefined' && dt.editRowIndex > -1) ? dt.editRowIndex : -1;
      return ind > -1 ? values[dt.field][ind] : null;
    }
    return values;
  }

  getValue(field: IComponent | string | undefined, arrayInd: number = -1): any {
    if (typeof field === 'undefined') {
      console.error('getValue: field is undefined');
      return undefined
    }
    let comp: IDataComponent
    if (typeof field === 'string') {
      const temp = this.getCompByField(field)
      if (temp) {
        comp = temp as IDataComponent
      } else {
        console.error('component for field not found ! name: ', field);
        return undefined
      }
    } else {
      comp = field as IDataComponent
    }
    // const c = comp as IDataComponent
    if (!comp.field) {
      console.error('field not specified ! name: ', comp.name);
      return undefined;
    }
    if ((comp as IComponent).type === 'datatable') {
      const { val } = this.getValuesDataTable(comp as IDataTableComponent)
      return val
    }

    let Values = comp.unbound ? this.MemValues : this.Values
    Values = this.getParentValues(comp, Values, arrayInd);

    const val = get(Values, comp.field);

    if (typeof val === 'undefined') {
      if ((comp as IComponent).type === 'checkbox') {
        return false;
      }
      return '';
    }

    if (comp.dataType) {
      if (comp.dataType === 'int' && val) {
        const v = parseInt(val);
        if (isNaN(v)) return null;
        return v
      }

      if (comp.dataType === 'float' && val) {
        const v = parseFloat(val);
        if (isNaN(v)) return null;
        return v
      }

      if (comp.dataType === 'date' && val) {
        return new Date(val)
      }
    }

    return val;
  }

  updateValue(field: IComponent | string | undefined, val: any, arrayInd: number = -1): void {
    if (typeof field === 'undefined') {
      console.error('updateValue: field is undefined');
      return undefined
    }

    let comp: IDataComponent
    if (typeof field === 'string') {
      const temp = this.getCompByField(field)
      if (temp) {
        comp = temp as IDataComponent
      } else {
        console.error('component for field not found ! name: ', field);
        return undefined
      }
    } else {
      comp = field as IDataComponent
    }

    if (!comp.field) {
      console.error('field not specified ! name: ', comp.name);
      return;
    }

    let Values = comp.unbound ? this.MemValues : this.Values
    Values = this.getParentValues(comp, Values, arrayInd);

    if ((comp as IComponent).type === 'datatable') {
      if (!val) val = []
    } else if (comp.dataType === 'float') {
      val = parseFloat(val);
      if (isNaN(val)) val = null;

    } else if (comp.dataType === 'int') {
      val = parseInt(val);
      if (isNaN(val)) val = null;
    }

    const curVal = get(Values, comp.field);
    if (curVal === val) return;
    set(Values, comp.field, val);

    this.ValuesChanged = true;

    if (comp.onChange) {
      comp.onChange({ sm: this, comp: (comp as IComponent), value: val });
    }

    if (this.Schema.onChange) {
      this.Schema.onChange({ sm: this, comp: (comp as IComponent), value: val });
    }

    if (this.InitDiffHighlightComp((comp as IComponent), arrayInd)) {
      if (arrayInd > -1) {
        const dt = this.getParentDataTable(comp)
        this.RenderComponent(dt)
      } else {
        this.RenderComponent(comp as IComponent)
      }
    }

  }

  validate(comp: IDataComponent, value: any, arrayInd: number = -1): string | undefined {

    if (arrayInd === -1) {
      const pd = this.getParentDataTable(comp)
      if (pd && typeof pd.editRowIndex !== 'undefined') arrayInd = pd.editRowIndex;
    }

    let err
    this.removeErrors(comp, arrayInd);
    if (this.hasNoValue(value) && this.getPropValue(comp as IComponent, 'required')) {
      err = this.Strings.required
      this.addError(comp, err, arrayInd);
    } else if (comp.validate) {
      const c = comp as IComponent
      err = comp.validate({ sm: this, comp: c, value });
      this.addError(comp, err, arrayInd);
    }
    return err
  }

  validateAll(rendercomponent: boolean = true) {
    this.Errors = []
    // this.validate(this.Schema as IDataComponent, '');
    this.traverseSchema(cp => {
      const c = cp as IDataComponent
      if (c.field) {
        if (cp.type === 'datatable') {
          const arrVal = get(this.Values, c.field);
          this.validate(c, arrVal);
          const typ = SchemaManager.checkValueType(arrVal);
          if (typ === IValueType.array) {
            arrVal.forEach((_: any, ind: number) => {
              this.traverseSchema(dtc => {
                if (dtc['field']) {
                  const value = this.getValue(dtc, ind);
                  this.validate(dtc as IDataComponent, value, ind);
                }
              }, undefined, cp)
            })
          }
        } else if (!this.fieldIsInDataTable(c)) {
          const value = this.getValue(cp);
          this.validate(c, value);
        }
      }
    });
    if (this.Schema.validate) {
      const errs = this.Schema.validate({ sm: this, comp: this.Schema });
      this.addError(this.Schema as IDataComponent, errs);
    }
    this.AllValidated = true;
    if (rendercomponent) this.RenderComponent()
  }

  private addError(comp: IDataComponent, error: string | undefined, arrayInd?: number) {
    if (this.hasNoValue(error)) return;
    if (error) {
      this.Errors.push({ comp, arrayInd, error })
    }
  }

  private removeErrors(comp: IDataComponent, arrayInd: number) {
    this.Errors = this.Errors.filter(e => !(e.comp === comp && e.arrayInd === arrayInd))
    // const ind = this.Errors.findIndex(e => e.comp === comp && e.arrayInd === arrayInd);
    // if (ind > -1) {
    //   this.Errors.splice(ind, 1);
    // }
  }

  removeAllErrors() {
    this.Errors = [];
  }

  getError(comp: IDataComponent) {
    const pd = this.getParentDataTable(comp)
    const arrayInd = pd && typeof pd.editRowIndex !== 'undefined' ? pd.editRowIndex : -1;
    const error = this.Errors.find(e => e.comp === comp && e.arrayInd === arrayInd);
    return error ? error.error : '';
  }

  getCompByName(name: string, schema?: IComponent): IComponent | undefined {
    let comp;
    const o: ITraverseOptions = { done: false };
    this.traverseSchema(c => {
      if (c.name === name) {
        o.done = true;
        comp = c;
      }
    }, o, schema || this.Schema);
    return comp;
  }

  getCompByField(field: string, schema?: IComponent): IComponent | undefined {
    let comp;
    const o: ITraverseOptions = { done: false };
    this.traverseSchema(cp => {
      const c = cp as IDataComponent
      if (c.field === field) {
        o.done = true;
        comp = c
      };
    }, o, schema || this.Schema);
    return comp;
  }

  selectOptionsAsObjects(comp: IComponent): ISelectOptionItem[] {
    const val = this.getPropValue(comp, 'options');
    if (!val || !Array.isArray(val) || val.length === 0) return [];
    let ret: ISelectOptionItem[] = [];
    if (typeof val[0] === "string") {
      val.forEach(item => ret.push({ value: item, label: item }));
      return ret;
    } else {
      return val;
    }
  }

  selectOptionsAsStrings(comp: IComponent): string[] {
    const val = this.getPropValue(comp, 'options');
    if (!val || !Array.isArray(val) || val.length === 0) return [];
    let ret: string[] = [];
    if (typeof val[0] === "object") {
      val.forEach(item => ret.push(item.text));
      return ret;
    } else {
      return val;
    }
  }

  // getColsClass(comp: IComponent, prop: string = 'cols'): string {
  //   let ret: string = this.getPropValue(comp, prop) || '';

  //   const getCol = (size: string, prev: string): string => {
  //     return ret.indexOf(size) > -1 ? ret.substr(ret.indexOf(size) + 1, 2).trim() : prev
  //   }
  //   const s = getCol('s', '12')
  //   const m = getCol('m', s)
  //   const l = getCol('l', m)
  //   const xl = getCol('x', l)

  //   return `col s${s} m${m} l${l} xl${xl}`
  // }

  getCompLabel(comp: IComponent): string {
    if (comp['label']) return comp['label']
    if (comp.parentComp && comp.parentComp.type === 'field') {
      return comp.parentComp.label
    }
    if (comp['placeholder']) return comp['placeholder']
    if (comp['field']) return comp['field']
    return ''
  }


  FocusComponent(comp: IComponent, arrayInd: number = -1) {
    const ok = this.MakeVisible(comp, arrayInd);
    //timeout since maybe rendercomponent called above
    setTimeout(() => {
      if (ok) this.emit(EventType.FocusComponent, comp)
    }, 100);
  }

  public RenderComponent(comp?: IComponent) {
    const c = comp ? comp : this.Schema
    this.emit(EventType.RenderComponent, c)
  }

  public RenderComponentByName(name: string) {
    const comp = this.getCompByName(name)
    if (comp) {
      this.emit(EventType.RenderComponent, comp)
    }
  }

  public RenderComponentByField(field: string) {
    const comp = this.getCompByField(field)
    if (comp) {
      this.emit(EventType.RenderComponent, comp)
    }
  }

  public OpenModal(name: string) {
    const comp = this.getCompByName(name)
    if (comp) {
      this.emit(EventType.OpenModal, comp)
    }
  }

  public CloseModal(name: string) {
    const comp = this.getCompByName(name)
    if (comp) {
      this.emit(EventType.CloseModal, comp)
    }
  }

  public FocusDropDown(comp?: IComponent) {
    if (comp) {
      this.emit(EventType.FoucsDropdown, comp)
    }
  }


  IsCompDisabled(comp: IComponent): boolean {
    if (typeof comp.disabled === 'undefined') {
      if (this.AllDisabled) {
        return true
      }
    } else {
      return this.getPropValue(comp, 'disabled')
    }
    return false
  }

  MakeVisible(comp: IComponent, arrayInd: number): boolean {
    let curTab: any;
    let cur = comp;
    let ok = true;

    while (cur && cur.parentComp && ok) {
      if (this.Schema.onMakeVisible) ok = this.Schema.onMakeVisible({ sm: this, comp: cur });
      if (!ok) continue;
      if (cur.parentComp.type == 'expansionspanel') {
        ok = !this.IsCompDisabled(cur.parentComp)
        if (ok) cur.parentComp.expanded = true;
        this.RenderComponent(cur.parentComp)
      } else if (cur.parentComp.type == 'tab') {
        curTab = cur.parentComp;
      } else if (cur.parentComp.type == 'tabs') {
        if (curTab && cur.parentComp.children && Array.isArray(cur.parentComp.children)) {
          ok = !this.IsCompDisabled(curTab)
          if (ok) {
            const ind = cur.parentComp.children.indexOf(curTab);
            cur.parentComp.selectedTabIndex = ind;
            this.RenderComponent(cur.parentComp)
          }
        }
      } else if (this.fieldIsInDataTable(cur as IDataComponent)) {
        let p: IComponent | undefined = cur.parentComp;
        while (p && p.type !== 'datatable') {
          p = p.parentComp;
        }
        if (p && p.type === 'datatable') {
          this.updateEditRowIndex(p as IDataTableComponent, arrayInd)
          this.RenderComponent(p)
        }

      }
      cur = cur.parentComp;
    }
    return ok;

  }

  traverseSchema(fn: ITraverseCallback, options?: ITraverseOptions, comp?: IComponent, parentComp?: IComponent) {
    if (options && options.done) return;

    if (!comp) comp = this.Schema;

    fn(comp, parentComp, options);
    const ctcomp = comp as IContainerComponent
    if (ctcomp.children) {
      ctcomp.children.forEach(c => this.traverseSchema(fn, options, c, comp));
    }
    Object.keys(comp).forEach(prop => {
      if (comp && prop.indexOf('Component') > - 1) {
        const cp = comp[prop]
        if (SchemaManager.checkValueType(cp) === IValueType.component) {
          this.traverseSchema(fn, options, cp, comp)
        }
      }
    })
  }

  static compareArray(val1: string[], val2: string[]): boolean {
    if (val1.length !== val2.length) {
      return false;
    }
    for (let i = 0; i < val1.length; i++) {
      if (val1[i] !== val2[i]) {
        console.log('here', val1[i], val2[i])
        return false;
      }
    }
    return true;

  }

  static checkValueType(val: any): IValueType {
    if (typeof val === 'undefined') {
      return IValueType.undefined;
    } else if (val === null) {
      return IValueType.null;
    } else if (val instanceof Date) {
      return IValueType.date;
    } else if (typeof val === 'string') {
      return IValueType.string;
    } else if (typeof val === 'number') {
      return IValueType.number;
    } else if (typeof val === 'boolean') {
      return IValueType.boolean;
    } else if (typeof val === 'function') {
      return IValueType.function;
    } else if (Array.isArray(val)) {
      return IValueType.array;
    } else if (typeof val === 'object') {
      if (val.type) {
        return IValueType.component;
      } else {
        return IValueType.object;
      }
    }
    return IValueType.undefined;
  }

  static checkSchema(schema: ISchema, ignoreTypes?: string[], checkNameOrField?: boolean): ISchemaError[] {
    return checkSchema(schema, ignoreTypes, checkNameOrField)
  }

  static printErrors = (schemaName: string, errors: ISchemaError[]) => {
    return printErrors(schemaName, errors)
  }


  checkOptionsValueType(val: any[]): boolean {
    if (SchemaManager.checkValueType(val) !== IValueType.array) return false
    if (val.length === 0) return true
    const typ = SchemaManager.checkValueType(val[0]);
    if (!(typ === IValueType.string || typ === IValueType.object)) return false
    let ok = true
    if (typ === IValueType.string) {
      const nok = !!val.find(o => SchemaManager.checkValueType(o) !== IValueType.string)
      ok = !nok
    } else {
      let nok = !!val.find(o => SchemaManager.checkValueType(o) !== IValueType.object)
      ok = !nok
      if (ok) nok = !!val.find(o => (SchemaManager.checkValueType(o.value) === IValueType.undefined || SchemaManager.checkValueType(o.text) === IValueType.undefined))
      ok = !nok
    }
    return ok
  }



  getObjectId(comp: IComponent): string {
    if (this.compIds.has(comp)) {
      return this.compIds.get(comp);
    } else {
      const id = String(this.curCompId++);
      this.compIds.set(comp, id);
      return id;
    }
  }

  // Datatable
  getValuesDataTable(comp: IDataTableComponent): IValuesDataTable {
    let val = get(this.Values, comp.field!);
    let memval = get(this.MemValues, comp.field!);
    if (!val) {
      val = []
      set(this.Values, comp.field!, val)
    }
    if (!memval) {
      memval = []
      for (let ind = 0; ind < val.length; ind++) {
        memval.push({})
      }
      set(this.MemValues, comp.field!, memval)
    }
    return { val, memval }


  }

  private InitDataTableKeyField(comp: IDataTableComponent) {
    const keyfield: IComponent = {
      type: 'input',
      dataType: 'int',
      hidden: true,
      unbound: true,
      field: this.getDataTableKeyField(comp)
    }
    const dtc = comp.detailComponent as IContainerComponent
    dtc && dtc.children.push(keyfield as IComponent)
    keyfield.parentComp = comp
  }

  private InitValuesDataTable(comp: IDataTableComponent, arrayInd: number = -1) {
    if (comp.type !== 'datatable') return;

    const keyField = this.getDataTableKeyField(comp)

    const { memval } = this.getValuesDataTable(comp)

    if (arrayInd > -1) {
      // New Row added
      let keyCounter = 0;
      memval.forEach((_r: any, ind: number) => {
        const k = get(memval[ind], keyField)
        if (keyCounter < k) keyCounter = k
      });
      keyCounter++
      set(memval[arrayInd], keyField, keyCounter)

      this.traverseSchema((cp: IComponent) => {
        const c = cp as IDataComponent
        if (c.field && c.hasOwnProperty('default')) {
          const val = this.getValue(cp, arrayInd)
          if (typeof val === 'undefined' || val === '' || val === false) {
            this.updateValue(cp, this.getPropValue(cp, 'default'), arrayInd)
          }
        }
      }, undefined, comp.detailComponent as IComponent)

    } else {
      //Init key
      memval.forEach((_r: any, ind: number) => {
        set(memval[ind], keyField, ind + 1)
      });
    }

  }

  InsertDataTableRow(comp: IDataTableComponent, copyInd: number = -1) {
    this.CommitChangesDataTable(comp)
    const { val, memval } = this.getValuesDataTable(comp)
    const r = copyInd > -1 && val.length > copyInd ? cloneDeep(val[copyInd]) : {}

    val.push(r)
    memval.push({})
    const len = val.length - 1
    this.InitValuesDataTable(comp, len)
    this.updateEditRowIndex(comp, len)
    this.RenderComponent(comp)
    if (comp.detailComponent.type === 'modal') {
      this.OpenModal(comp.detailComponent.name!)
    }
  }

  EditDataTableRow(comp: IDataTableComponent, ind: number) {
    this.CommitChangesDataTable(comp)
    this.updateEditRowIndex(comp, ind)
    this.RenderComponent(comp)
    if (comp.detailComponent.type === 'modal') {
      this.OpenModal(comp.detailComponent.name!)
    }
  }

  DeleteDataTableRow(comp: IDataTableComponent, ind: number = -1) {
    const { val, memval } = this.getValuesDataTable(comp)
    if (val && memval && ind < val.length && ind < memval.length) {
      val.splice(ind, 1)
      memval.splice(ind, 1)
    }
    this.updateEditRowIndex(comp, -1)
    this.RenderComponent(comp)
  }

  public CloseDetailComponent(comp: IDataComponent) {
    const dt = this.getParentDataTable(comp)
    if (dt) {
      this.updateEditRowIndex(dt, -1)
      this.RenderComponent(dt)
    }
  }


  getDataTableKeyField(comp: IDataComponent) {
    return comp.field + '_key_'
  }

  getParentDataTable(comp: IDataComponent): IDataTableComponent | undefined {
    let p = comp.parentComp;
    while (p) {
      if (p.type === 'datatable') {
        return p as IDataTableComponent;
      }
      p = p.parentComp;
    }
    return undefined;
  }

  fieldIsInDataTable(comp: IDataComponent): boolean {
    const c = this.getParentDataTable(comp)
    return (c !== undefined)
  }

  getFieldsOfDataTable(comp: IDataTableComponent): IComponent[] {
    const arr: IComponent[] = []
    this.traverseSchema(c => {
      if ((c as IDataComponent).field && c.type !== 'datatable' && !c.hidden) {
        arr.push(c)
      }
    }, undefined, comp)
    let res: IComponent[] = []
    if (comp.columns) {
      comp.columns.forEach(str => {
        const c = arr.find(c => (c as IDataComponent).field === str)
        if (c) {
          res.push(c)
        }
      })
    } else {
      res = arr
    }
    return res

  }

  public CommitChangesDataTable(comp: IComponent) {
    if (comp.type === 'datatable' && comp.field) {
      const { val, memval } = this.getValuesDataTable(comp as IDataTableComponent)

      if (!this.CommitValuesDataTable) this.CommitValuesDataTable = {}
      if (!this.CommitValuesDataTable.Values) this.CommitValuesDataTable.Values = {}
      if (!this.CommitValuesDataTable.MemValues) this.CommitValuesDataTable.MemValues = {}
      this.CommitValuesDataTable.Values[comp.field] = cloneDeep(val)
      this.CommitValuesDataTable.MemValues[comp.field] = cloneDeep(memval)
    }
  }

  public SaveChangesDataTable(comp: IDataTableComponent) {
    if (comp.type !== 'datatable' || !comp.field || !comp.detailComponent) return
    if (comp.detailComponent.type === 'modal') {
      this.CloseModal(comp.detailComponent.name!)
    }
    this.updateEditRowIndex(comp, -1)
    this.RenderComponent(comp)
  }

  public RevertChangesDataTable(comp: IDataTableComponent) {
    if (comp.type !== 'datatable' || !comp.field || !comp.detailComponent) return
    if (comp.detailComponent.type === 'modal') {
      this.CloseModal(comp.detailComponent.name!)
    }
    const v = this.Values
    const cv = this.CommitValuesDataTable
    if (v && v[comp.field] && cv && cv.Values && cv.Values[comp.field]) {
      v[comp.field] = cloneDeep(cv.Values[comp.field])
    }

    const mv = this.MemValues
    if (mv && mv[comp.field] && cv && cv.MemValues && cv.MemValues[comp.field]) {
      mv[comp.field] = cloneDeep(cv.MemValues[comp.field])
    }
    this.updateEditRowIndex(comp, -1)
    this.RenderComponent(comp)
  }

  private updateEditRowIndex(comp: IDataTableComponent, ind: number) {
    comp.editRowIndex = ind;
  }


}
